<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppButton from '@uiKit/elements/Button.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import AmountInputField from './AmountInputField.vue'
import SelectBankModal from './SelectBankModal.vue'
import { maskAccountNumber } from '@shared/utils'
import type { BankAccount } from '@modules/account'

const DEFAULT_WITHDRAWAL_AMOUNT = 5000
const PRESET_FRACTIONS = [0.25, 0.5, 0.75]

interface Props {
  balance?: number
  defaultBankAccount?: BankAccount | null
  accounts?: BankAccount[]
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  balance: 0,
  defaultBankAccount: null,
  accounts: () => [],
  loading: false,
})

const emit = defineEmits<{
  (e: 'confirm', payload: { amount: number; bankAccountId: string }): void
  (e: 'cancel'): void
  (e: 'add-bank'): void
}>()

function initialAmount(balance: number): number {
  if (balance <= 0) return 0
  return Math.min(DEFAULT_WITHDRAWAL_AMOUNT, balance)
}

const amount = ref(initialAmount(props.balance))
const selectedBank = ref<BankAccount | null>(props.defaultBankAccount)
const modalVisible = ref(false)
const clientError = ref('')

watch(
  () => props.defaultBankAccount,
  (val) => {
    if (!selectedBank.value) selectedBank.value = val
  },
)

watch(
  () => props.balance,
  (newBalance, oldBalance) => {
    if (oldBalance === 0 && newBalance > 0) {
      amount.value = initialAmount(newBalance)
    } else if (amount.value > newBalance) {
      amount.value = newBalance
    }
  },
)

watch(amount, () => {
  if (clientError.value) clientError.value = ''
})

watch(selectedBank, () => {
  if (clientError.value) clientError.value = ''
})

function formatAmount(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const remainingBalance = computed(() => Math.max(0, props.balance - amount.value))
const isOverBalance = computed(() => amount.value > 0 && amount.value > props.balance)

const balancePresets = computed(() => {
  if (props.balance <= 0) return []
  return [
    ...PRESET_FRACTIONS.map((fraction) => ({
      value: Math.floor(props.balance * fraction),
      label: `${Math.round(fraction * 100)}%`,
    })),
    { value: props.balance, label: 'Max' },
  ]
})

function validate(): string {
  if (amount.value <= 0) return 'Enter an amount greater than AED 0.'
  if (isOverBalance.value) {
    return `Amount exceeds your available balance of AED ${formatAmount(props.balance)}.`
  }
  if (!selectedBank.value) return 'Add a bank account to receive the funds.'
  return ''
}

function handleConfirm() {
  const message = validate()
  if (message) {
    clientError.value = message
    return
  }
  const bank = selectedBank.value
  if (!bank) {
    clientError.value = 'Add a bank account to receive the funds.'
    return
  }
  clientError.value = ''
  emit('confirm', { amount: amount.value, bankAccountId: bank.id })
}

function handleBankAction() {
  if (selectedBank.value) {
    modalVisible.value = true
  } else {
    emit('add-bank')
  }
}

const hasBank = computed(() => selectedBank.value !== null)
const bankName = computed(() => selectedBank.value?.bankName ?? 'No bank on file')
const maskedAccount = computed(() =>
  selectedBank.value ? maskAccountNumber(selectedBank.value.accountNumber) : 'Add one to continue',
)
const bankLabel = computed(() => (selectedBank.value?.isPrimary ? 'Default' : ''))
</script>

<template>
  <Box direction="col" gap="4" class="wf">
    <Box direction="col" gap="4" class="wf__body">
      <AmountInputField v-model="amount" :max="balance" :presets="balancePresets" />

      <Box direction="col" gap="2" class="wf__field">
        <Text type="caption" color="muted" weight="medium" is="span">Transfer to</Text>
        <Box align="center" gap="3" class="wf__bank-row">
          <Box flex align="center" justify="center" class="wf__bank-icon-box">
            <PiIcon name="building-columns" style="font-size: 16px; color: var(--p-primary-600)" />
          </Box>
          <Box direction="col" gap="1" grow class="wf__bank-info">
            <Text type="body" weight="semibold" is="span">{{ bankName }}</Text>
            <Text type="caption" weight="medium" color="muted" is="span">
              {{ maskedAccount }}{{ bankLabel ? ` · ${bankLabel}` : '' }}
            </Text>
          </Box>
          <Box
            is="button"
            class="wf__change-btn"
            type="button"
            :aria-label="hasBank ? 'Change bank account' : 'Add bank account'"
            @click="handleBankAction"
          >
            <Text type="caption" weight="medium" color="primary" is="span">
              {{ hasBank ? 'Change' : 'Add bank' }}
            </Text>
          </Box>
        </Box>
      </Box>

      <Box direction="col" gap="2" class="wf__summary">
        <Box align="center" justify="between" gap="3">
          <Text type="body" color="muted" is="span">Withdrawal amount</Text>
          <Text type="body" weight="medium" is="span">AED {{ formatAmount(amount) }}</Text>
        </Box>
        <Box align="center" justify="between" gap="3">
          <Text type="body" color="muted" is="span">Processing fee</Text>
          <Text type="body" weight="medium" color="success" is="span">Free</Text>
        </Box>
        <Box class="wf__divider" />
        <Box align="center" justify="between" gap="3">
          <Text type="subheading" is="span">Remaining balance</Text>
          <Text
            type="subheading"
            weight="bold"
            :color="clientError && isOverBalance ? 'danger' : 'default'"
            is="span"
          >
            AED {{ formatAmount(remainingBalance) }}
          </Text>
        </Box>
      </Box>

      <Box v-if="clientError" align="center" gap="1" class="wf__field-error">
        <PiIcon name="exclamation-circle" class="wf__field-error-icon" />
        <Text type="caption" color="danger" is="span">{{ clientError }}</Text>
      </Box>
    </Box>

    <Box class="wf__spacer" />

    <Box direction="col" gap="3" class="wf__actions">
      <AppButton
        label="Confirm Withdrawal"
        block
        size="large"
        :loading="loading"
        @click="handleConfirm"
      />
      <AppButton label="Cancel" variant="text" color="secondary" block @click="emit('cancel')" />
    </Box>

    <Box align="center" justify="center" gap="2" class="wf__trust">
      <PiIcon name="clock" style="font-size: 14px; color: var(--p-text-muted-color)" />
      <Text type="caption" color="muted" is="span">
        Funds arrive in your bank within 48 hours
      </Text>
    </Box>

    <SelectBankModal
      v-model:visible="modalVisible"
      :accounts="accounts"
      :selected-id="selectedBank?.id ?? ''"
      @select="selectedBank = $event"
    />
  </Box>
</template>

<style scoped>
.wf {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1.25rem 1.5rem 1.5rem;
  box-sizing: border-box;
}

.wf__body {
  flex-shrink: 0;
}

.wf__field {
  min-width: 0;
}

.wf__field-error {
  margin-top: 0.125rem;
}

.wf__field-error-icon {
  color: var(--p-danger-600);
  font-size: 12px;
}

.wf__spacer {
  flex: 1;
  min-height: 1rem;
}

.wf__bank-row {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  padding: 0.75rem 0.875rem;
}

.wf__bank-icon-box {
  width: 36px;
  height: 36px;
  background: var(--p-primary-50);
  border: 1px solid var(--p-primary-100);
  border-radius: var(--p-border-radius-sm);
  flex-shrink: 0;
}

.wf__bank-info {
  min-width: 0;
}

.wf__change-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.wf__change-btn:hover {
  opacity: 0.75;
}

.wf__change-btn:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
  border-radius: var(--p-border-radius-sm);
}

.wf__summary {
  background: var(--p-surface-50);
  border-radius: var(--p-border-radius-md);
  padding: 0.875rem 1rem;
}

.wf__divider {
  height: 1px;
  background: var(--p-surface-200);
  margin: 0.25rem 0;
}

.wf__actions {
  flex-shrink: 0;
}

.wf__trust {
  flex-shrink: 0;
  text-align: center;
}
</style>
